import React, { useState } from "react";

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Chip,
  IconButton,
  // CircularProgress,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionAnalyzeFundTable, IFile } from "store/types";

import { FileType } from "../../../constants";

interface AnalyzeFundTableSectionProps {
  section: DocumentSectionAnalyzeFundTable;
}

const StyledTableHeadCell = styled(TableCell)({
  fontSize: "16px",
  fontWeight: 400,
  color: "#071F35",
  padding: "10px 10px",
  whiteSpace: "nowrap",
  borderBottom: "none",
  maxHeight: "40px",
  maxWidth: "100px"
});

const StyledTableRowCell = styled(TableCell)({
  fontSize: "14px",
  padding: "10px 10px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100px",
  borderBottom: "1px solid #E5E9EB"
});

const StyledText = styled(Typography)({
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  textAlign: "center",
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: 400
});

const formatCellValue = (value: number | string | null, key: string): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (key === "size") {
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numericValue)) {
      return String(value);
    }
    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  return String(value);
};

export const AnalyzeFundTableSection: React.FC<AnalyzeFundTableSectionProps> = ({ section }) => {
  const [pdf, setPdf] = useState<Pick<IFile, "file_url" | "type"> | null>(null);

  if (section.report_data && (!section.report_data.columns || !section.report_data.rows)) {
    return (
      <Stack
        sx={{
          width: "100%",
          overflow: "hidden",
          borderRadius: "16px",
          gap: "8px",
          padding: "16px",
          boxShadow: "0px 1px 4px 0px #1212171F"
        }}
      >
        <Stack sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071F35" }}>
            Couldn’t load file data to build the Compare Funds table.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "16px",
        gap: "8px",
        padding: "16px",
        boxShadow: "0px 1px 4px 0px #1212171F"
      }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 500,
            color: "#071F35"
          }}
        >
          {section.title}
        </Typography>
        {section?.file?.file_url && (
          <IconButton onClick={() => setPdf({ file_url: section?.file?.file_url, type: FileType.PDF })}>
            <MaterialTooltip title={section?.file?.name} isShow>
              <InsertDriveFileOutlinedIcon />
            </MaterialTooltip>
          </IconButton>
        )}
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          height: "100%",
          border: "1px solid #DBE8F5",
          borderRadius: "8px",
          boxShadow: "none",
          position: "relative"
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead sx={{ backgroundColor: "rgba(246, 248, 252, 1)" }}>
            <TableRow sx={{ height: "40px" }}>
              {section?.report_data?.columns?.map(({ key, name }) => (
                <StyledTableHeadCell key={key}>
                  <MaterialTooltip title={name} isShow>
                    <StyledText sx={{ textAlign: "center" }}>{name}</StyledText>
                  </MaterialTooltip>
                </StyledTableHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {section.report_data?.rows?.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {section.report_data?.columns?.length &&
                  section.report_data?.columns?.map(({ key }) => {
                    const cellData = Array.isArray(row) ? row?.find((cell) => cell?.key === key) || null : null;
                    if (!cellData) {
                      return (
                        <StyledTableRowCell key={key}>
                          <StyledText sx={{ textAlign: "center" }}>-</StyledText>
                        </StyledTableRowCell>
                      );
                    }

                    if (typeof cellData.value === "boolean") {
                      const label = cellData.value ? "Wins" : "Loses";
                      return (
                        <StyledTableRowCell key={key}>
                          <MaterialTooltip title={cellData.tooltip || label} isShow>
                            <Chip
                              label={label}
                              sx={{
                                backgroundColor: cellData.value ? "#E5F5E1" : "#FEE4E2",
                                color: cellData.value ? "#15803D" : "#B42318"
                              }}
                            />
                          </MaterialTooltip>
                        </StyledTableRowCell>
                      );
                    }
                    const formattedValue = formatCellValue(cellData.value, key);
                    const valueToShow = cellData.currency ? cellData.currency + " " + formattedValue : formattedValue;

                    return (
                      <StyledTableRowCell key={key}>
                        <MaterialTooltip title={valueToShow} isShow>
                          <StyledText sx={{ textAlign: "center" }}>{valueToShow}</StyledText>
                        </MaterialTooltip>
                      </StyledTableRowCell>
                    );
                  })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pdf?.file_url && <FileViewer file={pdf} isOpen={!!pdf} onClose={() => setPdf(null)} />}
    </Stack>
  );
};
